<template>
  <profile-edit-section-card
    v-if="localState"
    title="Specialty Skills"
    breadcrumb-label="Profile"
    :saving="isSaving"
    :has-unsaved-changes="hasUnsavedChanges"
    @breadcrumb-click="cancelEdit"
    @save="handleSave"
    @cancel="cancelEdit"
  >
    <fieldset class="mb-24">
      <legend class="form__label pb-8">Languages spoken (optional)</legend>
      <MultiselectComboBox
        id="languagesSpoken"
        :preselected-options="localState.languagesSpoken || []"
        :options="languageOptions"
        label-name="Languages spoken"
        @selected-options="handleLanguageUpdate"
      />
    </fieldset>
    <fieldset>
      <legend class="form__label pb-8">Qualifications (optional)</legend>
      <ul class="qualifications-grid">
        <li v-for="option in qualificationOptions" :key="option.value" class="mb-8">
          <PoppinsCheckbox v-model="localState.qualifications" :label-name="option.label" :value="option.value" />
        </li>
      </ul>
    </fieldset>
  </profile-edit-section-card>
</template>

<script lang="ts">
import PoppinsCheckbox from '@/components/checkbox/Checkbox.vue';
import ProfileEditSectionCard from '../../components/ProfileEditSectionCard.vue';
import MultiselectComboBox from '@/components/multiselect_combo_box/MultiselectComboBox.vue';
import profileEditMixin from '../../mixins/profileEditMixin';
import { languageMap } from '@/apps/provider/profile/__types__/SkillsTypes';

// Inlined to avoid CareTypeFormOptions circular-import issues at module init.
const QUALIFICATION_OPTIONS = [
  { value: 'REGISTERED_NURSE', label: 'Registered Nurse' },
  { value: 'CERTIFIED_NURSING_ASSISTANT', label: 'Certified Nursing Assistant' },
  { value: 'LICENSED_PRACTICAL_NURSE', label: 'Licensed Practical Nurse' },
  { value: 'LICENSED_VOCATIONAL_NURSE', label: 'Licensed Vocational Nurse' },
  { value: 'OCCUPATIONAL_THERAPIST', label: 'Occupational Therapist' },
  { value: 'PHYSICAL_THERAPIST', label: 'Physical Therapist' },
  { value: 'SPEECH_THERAPIST', label: 'Speech Therapist' },
  { value: 'BONDED', label: 'Bonded' },
  { value: 'LICENSED', label: 'Licensed' },
  { value: 'INSURED', label: 'Insured' },
] as const;

const KNOWN_QUALIFICATION_VALUES = new Set<string>(QUALIFICATION_OPTIONS.map((option) => option.value));

export default {
  name: 'CompanionCareSpecialtySkillsForm',
  components: {
    ProfileEditSectionCard,
    MultiselectComboBox,
    PoppinsCheckbox,
  },
  mixins: [profileEditMixin],
  data() {
    return {
      localState: null as null | { languagesSpoken: string[]; qualifications: string[] },
      originalState: null as null | { languagesSpoken: string[]; qualifications: string[] },
      qualificationOptions: [...QUALIFICATION_OPTIONS],
      languageOptions: Object.entries(languageMap).map(([value, { text }]) => ({ value, display: text })),
    };
  },
  computed: {
    hasUnsavedChanges(): boolean {
      if (!this.localState || !this.originalState) return false;
      return JSON.stringify(this.localState) !== JSON.stringify(this.originalState);
    },
  },
  watch: {
    $route() {
      this.initializeFormState();
    },
    formState: {
      handler(newState) {
        if (newState) {
          this.initializeFormState();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeFormState();
  },
  methods: {
    uniqueValues(values: unknown): string[] {
      const list = Array.isArray(values) ? values : [];
      return Array.from(new Set(list.filter(Boolean)));
    },
    initializeFormState() {
      const state = this.formState as Record<string, any> | null;
      if (!state) return;

      this.localState = {
        languagesSpoken: this.uniqueValues(state.languagesSpoken || []),
        qualifications: this.uniqueValues(state.qualifications || []).filter((value) => KNOWN_QUALIFICATION_VALUES.has(value)),
      };
      this.originalState = JSON.parse(JSON.stringify(this.localState));
    },
    handleLanguageUpdate(languages: Array<{ value: string }>) {
      if (!this.localState) return;
      this.localState.languagesSpoken = this.uniqueValues(languages.map((language) => language.value));
    },
    async handleSave() {
      if (!this.localState || !this.hasUnsavedChanges) return;

      const otherQualifications = this.uniqueValues(
        ((this.formState as Record<string, any>)?.qualifications || []).filter(
          (value: string) => !KNOWN_QUALIFICATION_VALUES.has(value)
        )
      );

      await this.saveSection({
        languagesSpoken: this.uniqueValues(this.localState.languagesSpoken),
        qualifications: this.uniqueValues([...otherQualifications, ...this.localState.qualifications]),
      });
    },
  },
};
</script>

<style scoped>
.qualifications-grid {
  display: grid;
  grid-template-columns: 1fr;
}

@media (min-width: 640px) {
  .qualifications-grid {
    grid-template-columns: 1fr 1fr;
    column-gap: 2rem;
  }
}
</style>
